import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, AlertCircle, CalendarCheck } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Seo } from "../components/Seo";
import { SITE } from "../lib/site";
import { VEHICLE_DB, MAKES, YEARS, PROBLEM_TYPES } from "../lib/vehicles";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const TIME_SLOTS = [
  { value: "morning", label: "Morning (9am – 12pm)" },
  { value: "afternoon", label: "Afternoon (12pm – 3pm)" },
  { value: "evening", label: "Late afternoon (3pm – 5pm)" },
  { value: "flexible", label: "Flexible — call to confirm" },
];

const Field = ({ label, required, children, hint }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/60">
      {label}{required && <span className="text-[#FF3B30]"> *</span>}
    </span>
    <div className="mt-2">{children}</div>
    {hint && <div className="mt-1 text-white/40 text-xs">{hint}</div>}
  </label>
);

const inputCls =
  "w-full bg-[#0A0A0A] border border-white/10 focus:border-[#FF3B30] outline-none text-white text-base px-4 py-3 transition-colors placeholder:text-white/30";

export default function Booking() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    year: "",
    make: "",
    model: "",
    trim: "",
    make_other: "",
    model_other: "",
    problem_type: "",
    problem_details: "",
    preferred_date: "",
    preferred_time: "flexible",
  });
  const [state, setState] = useState({ status: "idle", error: null, id: null });

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: v };
      // reset dependents when parent dropdown changes
      if (k === "make") { next.model = ""; next.trim = ""; }
      if (k === "model") { next.trim = ""; }
      return next;
    });
  };

  const isOtherMake = form.make === "Other (write-in)";
  const models = useMemo(
    () => (form.make && !isOtherMake ? Object.keys(VEHICLE_DB[form.make]?.models || {}) : []),
    [form.make, isOtherMake]
  );
  const isOtherModel = form.model === "Other";
  const trims = useMemo(() => {
    if (!form.make || isOtherMake || !form.model) return [];
    return VEHICLE_DB[form.make]?.models?.[form.model] || [];
  }, [form.make, form.model, isOtherMake]);

  const submit = async (e) => {
    e.preventDefault();
    if (state.status === "submitting") return;
    setState({ status: "submitting", error: null, id: null });
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        year: form.year,
        make: isOtherMake ? (form.make_other || "Other") : form.make,
        model: isOtherMake || isOtherModel ? (form.model_other || form.model || "Other") : form.model,
        trim: form.trim || null,
        problem_type: form.problem_type,
        problem_details: form.problem_details,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || "flexible",
      };
      const res = await fetch(`${API}/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setState({ status: "success", error: null, id: data.id });
    } catch (err) {
      setState({ status: "error", error: err.message || "Something went wrong", id: null });
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="page-booking">
      <Seo
        path="/book"
        title="Book an Appointment — Dirty Wrenches Automotive"
        description="Book a service appointment at Dirty Wrenches Automotive in Hermiston, OR. Pick year, make, model, trim and tell us what's going on with your car. BMW, Audi, VW, Mercedes, Subaru, Porsche specialists."
      />

      <PageHeader
        code="// 07"
        kicker="Book"
        title={<>Book an<br />appointment.</>}
        subtitle="Pick the car, tell us what's wrong, choose a window. We'll call to confirm — usually inside one business day."
        testId="booking-header"
      />

      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-4 space-y-8">
            <div>
              <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase">// how it works</div>
              <h2 className="mt-3 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl leading-[0.95]">
                Three steps. No surprises.
              </h2>
              <ol className="mt-6 space-y-4 text-white/75 text-sm">
                {[
                  { n: "01", t: "Tell us the car & the issue", d: "Year, make, model, trim — and what's going on." },
                  { n: "02", t: "Pick a window", d: "Date + a rough time slot. We'll confirm by phone." },
                  { n: "03", t: "Drop it off", d: "We diagnose, quote, and only proceed once you say go." },
                ].map((s) => (
                  <li key={s.n} className="flex items-start gap-4">
                    <span className="font-display font-black text-[#FF3B30] text-xl tracking-tight w-8 shrink-0">{s.n}</span>
                    <div>
                      <div className="font-display font-bold uppercase tracking-tight text-base">{s.t}</div>
                      <div className="text-white/55 text-sm leading-relaxed">{s.d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="bg-[#1A1F24] border border-white/10 p-6">
              <div className="flex items-center gap-3">
                <CalendarCheck className="w-5 h-5 text-[#FF3B30]" />
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/60">Honest scheduling</div>
              </div>
              <p className="mt-3 text-white/70 text-sm leading-relaxed">
                We typically book two to three weeks out — that's the trade-off for a three-man shop. Quick diagnostics can sometimes slot in sooner; tell us what's wrong when you submit.
              </p>
            </div>

            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/60 mb-2">Prefer to call?</div>
              <a
                href={`tel:${SITE.contact.phone}`}
                data-testid="booking-tel-link"
                className="font-display font-black uppercase tracking-tight text-2xl text-white hover:text-[#FF3B30] transition-colors"
              >
                {SITE.contact.phone}
              </a>
              <div className="mt-1 text-white/50 text-sm">{SITE.contact.address}</div>
            </div>
          </aside>

          <div className="lg:col-span-8">
            {state.status === "success" ? (
              <div data-testid="booking-success" className="bg-[#0F1A12] border border-emerald-500/30 p-8 md:p-12">
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-emerald-400">// appointment received</div>
                <h3 className="mt-3 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">
                  Got it. We'll be in touch.
                </h3>
                <p className="mt-4 text-white/70 leading-relaxed max-w-xl">
                  We'll call you to confirm a slot, usually inside one business day. If it's urgent, give us a ring at <span className="text-white">{SITE.contact.phone}</span>.
                </p>
                <div className="mt-6 font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/40">
                  Reference: <span className="text-white/70">{state.id}</span>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setState({ status: "idle", error: null, id: null });
                      setForm((f) => ({ ...f, problem_details: "" }));
                    }}
                    data-testid="booking-new-btn"
                    className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white text-xs font-bold uppercase tracking-[0.25em] px-6 py-3 transition-colors"
                  >
                    Book another
                  </button>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF5722] text-white text-xs font-bold uppercase tracking-[0.25em] px-6 py-3 transition-colors"
                  >
                    Back to home <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ) : (
              <form data-testid="booking-form" onSubmit={submit} className="bg-[#0E0E0E] border border-white/10 p-6 md:p-10 space-y-6">
                {/* contact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Your name" required>
                    <input
                      data-testid="book-input-name"
                      className={inputCls}
                      value={form.name}
                      onChange={set("name")}
                      required
                      placeholder="First and last"
                    />
                  </Field>
                  <Field label="Phone" required>
                    <input
                      data-testid="book-input-phone"
                      className={inputCls}
                      value={form.phone}
                      onChange={set("phone")}
                      required
                      type="tel"
                      placeholder="(541) 555-0199"
                    />
                  </Field>
                </div>
                <Field label="Email" required>
                  <input
                    data-testid="book-input-email"
                    className={inputCls}
                    value={form.email}
                    onChange={set("email")}
                    required
                    type="email"
                    placeholder="you@example.com"
                  />
                </Field>

                {/* vehicle */}
                <div className="border-t border-white/10 pt-6">
                  <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase mb-4">// vehicle</div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <Field label="Year" required>
                      <select
                        data-testid="book-input-year"
                        className={inputCls}
                        value={form.year}
                        onChange={set("year")}
                        required
                      >
                        <option value="">Select…</option>
                        {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </Field>
                    <Field label="Make" required>
                      <select
                        data-testid="book-input-make"
                        className={inputCls}
                        value={form.make}
                        onChange={set("make")}
                        required
                      >
                        <option value="">Select…</option>
                        {MAKES.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </Field>
                    <Field label="Model" required>
                      {isOtherMake ? (
                        <input
                          data-testid="book-input-model-other"
                          className={inputCls}
                          value={form.model_other}
                          onChange={set("model_other")}
                          required
                          placeholder="e.g. F-150"
                        />
                      ) : (
                        <select
                          data-testid="book-input-model"
                          className={inputCls}
                          value={form.model}
                          onChange={set("model")}
                          required
                          disabled={!form.make}
                        >
                          <option value="">{form.make ? "Select…" : "Pick a make first"}</option>
                          {models.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                      )}
                    </Field>
                    <Field label="Trim">
                      {isOtherMake || isOtherModel ? (
                        <input
                          data-testid="book-input-trim-other"
                          className={inputCls}
                          value={form.trim}
                          onChange={set("trim")}
                          placeholder="Optional"
                        />
                      ) : (
                        <select
                          data-testid="book-input-trim"
                          className={inputCls}
                          value={form.trim}
                          onChange={set("trim")}
                          disabled={!form.model}
                        >
                          <option value="">{form.model ? "Optional / select" : "Pick a model first"}</option>
                          {trims.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      )}
                    </Field>
                  </div>
                  {isOtherMake && (
                    <div className="mt-4">
                      <Field label="Make (write-in)" required hint="We work mostly on European & Subaru imports, but bring it by — no job too small.">
                        <input
                          data-testid="book-input-make-other"
                          className={inputCls}
                          value={form.make_other}
                          onChange={set("make_other")}
                          required
                          placeholder="e.g. Ford, Toyota, Honda, Harley-Davidson"
                        />
                      </Field>
                    </div>
                  )}
                </div>

                {/* problem */}
                <div className="border-t border-white/10 pt-6">
                  <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase mb-4">// what's going on</div>
                  <Field label="Problem type" required>
                    <select
                      data-testid="book-input-problem-type"
                      className={inputCls}
                      value={form.problem_type}
                      onChange={set("problem_type")}
                      required
                    >
                      <option value="">Select the closest match…</option>
                      {PROBLEM_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <div className="mt-6">
                    <Field
                      label="Describe the issue"
                      required
                      hint="When it happens, sounds, what you've tried, any fault codes — the more detail the better."
                    >
                      <textarea
                        data-testid="book-input-problem-details"
                        className={`${inputCls} min-h-[150px] resize-y leading-relaxed`}
                        value={form.problem_details}
                        onChange={set("problem_details")}
                        required
                        placeholder={`Example:\nCheck engine light came on last week. Code P0301 — cyl 1 misfire. Idles rough when cold, smooths out warm. Plugs and coils replaced 8k miles ago.`}
                      />
                    </Field>
                  </div>
                </div>

                {/* schedule */}
                <div className="border-t border-white/10 pt-6">
                  <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase mb-4">// preferred window</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Preferred date" hint="We'll confirm by phone">
                      <input
                        data-testid="book-input-date"
                        type="date"
                        min={today}
                        className={inputCls}
                        value={form.preferred_date}
                        onChange={set("preferred_date")}
                      />
                    </Field>
                    <Field label="Preferred time">
                      <select
                        data-testid="book-input-time"
                        className={inputCls}
                        value={form.preferred_time}
                        onChange={set("preferred_time")}
                      >
                        {TIME_SLOTS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </div>

                {state.status === "error" && (
                  <div data-testid="booking-error" className="flex items-start gap-3 bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF8A82] p-4">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    <div className="text-sm">{state.error}</div>
                  </div>
                )}

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-2">
                  <div className="text-white/45 text-xs max-w-md leading-relaxed">
                    By submitting you agree we can contact you to confirm the appointment.
                  </div>
                  <button
                    data-testid="book-submit-btn"
                    type="submit"
                    disabled={state.status === "submitting"}
                    className="group inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5722] disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-[0.25em] px-7 py-4 transition-colors"
                  >
                    {state.status === "submitting" ? "Sending…" : "Request appointment"}
                    {state.status !== "submitting" && (
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          <Link
            to="/build"
            data-testid="booking-link-build"
            className="bg-[#0A0A0A] hover:bg-[#1A1F24] transition-colors p-8 md:p-10 flex items-start gap-5"
          >
            <Check className="w-6 h-6 text-[#FF3B30] mt-1" />
            <div>
              <div className="font-display font-black uppercase tracking-tight text-xl md:text-2xl">Got a build in mind?</div>
              <div className="text-white/55 text-sm mt-2 leading-relaxed">If you're planning mods, tunes or a full build, send those over on the Build & Consult page — different form, more space for the details.</div>
            </div>
          </Link>
          <Link
            to="/services"
            data-testid="booking-link-services"
            className="bg-[#0A0A0A] hover:bg-[#1A1F24] transition-colors p-8 md:p-10 flex items-start gap-5"
          >
            <Check className="w-6 h-6 text-[#FF3B30] mt-1" />
            <div>
              <div className="font-display font-black uppercase tracking-tight text-xl md:text-2xl">Not sure what you need?</div>
              <div className="text-white/55 text-sm mt-2 leading-relaxed">Check the full service list — diagnostics, performance, engine, brakes, maintenance — and bring us the symptom, we'll find the cause.</div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
